import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface Domain {
  id: string;
  name: string;
  description?: string;
  color: string;
  is_active: number;
  ruleCount: number;
  userCount: number;
  created_at?: string;
}

interface DomainUser {
  id: string;
  username: string;
  name: string;
  email?: string;
  global_role: string;
  domain_role: string;
}

const DomainManagement: React.FC = () => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [domainUsers, setDomainUsers] = useState<DomainUser[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#6366f1'
  });

  const colorOptions = [
    { value: '#6366f1', label: 'Purple' },
    { value: '#10b981', label: 'Green' },
    { value: '#f59e0b', label: 'Orange' },
    { value: '#ef4444', label: 'Red' },
    { value: '#3b82f6', label: 'Blue' },
    { value: '#8b5cf6', label: 'Violet' },
    { value: '#ec4899', label: 'Pink' },
    { value: '#14b8a6', label: 'Teal' }
  ];

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/domains?includeInactive=true`);
      setDomains((response.data as any).domains || []);
    } catch (error) {
      console.error('Error loading domains:', error);
      alert('Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  const loadDomainUsers = async (domainId: string) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/domains/${domainId}/users`);
      setDomainUsers((response.data as any).users || []);
    } catch (error) {
      console.error('Error loading domain users:', error);
      alert('Failed to load domain users');
    } finally {
      setLoading(false);
    }
  };

  const createDomain = async () => {
    if (!formData.name) {
      alert('Domain name is required');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_BASE}/domains`, formData);
      setShowCreateModal(false);
      setFormData({ name: '', description: '', color: '#6366f1' });
      await loadDomains();
      alert('Domain created successfully');
    } catch (error: any) {
      console.error('Error creating domain:', error);
      alert(error.response?.data?.error || 'Failed to create domain');
    } finally {
      setLoading(false);
    }
  };

  const updateDomain = async () => {
    if (!selectedDomain) return;

    try {
      setLoading(true);
      await axios.put(`${API_BASE}/domains/${selectedDomain.id}`, formData);
      setShowEditModal(false);
      setSelectedDomain(null);
      await loadDomains();
      alert('Domain updated successfully');
    } catch (error: any) {
      console.error('Error updating domain:', error);
      alert(error.response?.data?.error || 'Failed to update domain');
    } finally {
      setLoading(false);
    }
  };

  const deleteDomain = async (domainId: string) => {
    if (!window.confirm('Are you sure you want to deactivate this domain? Rules and users will not be deleted.')) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API_BASE}/domains/${domainId}`);
      await loadDomains();
      alert('Domain deactivated successfully');
    } catch (error: any) {
      console.error('Error deleting domain:', error);
      alert(error.response?.data?.error || 'Failed to deactivate domain');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (domain: Domain) => {
    setSelectedDomain(domain);
    setFormData({
      name: domain.name,
      description: domain.description || '',
      color: domain.color
    });
    setShowEditModal(true);
  };

  const openUsersModal = (domain: Domain) => {
    setSelectedDomain(domain);
    loadDomainUsers(domain.id);
    setShowUsersModal(true);
  };

  return (
    <div className="domain-management">
      <div className="management-header">
        <h2>Domain Management</h2>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          Create Domain
        </button>
      </div>

      {loading && <div className="loading">Loading...</div>}

      <div className="domain-grid">
        {domains.map(domain => (
          <div
            key={domain.id}
            className={`domain-card ${domain.is_active ? '' : 'inactive-card'}`}
            style={{ borderLeftColor: domain.color }}
          >
            <div className="domain-header">
              <div
                className="domain-color-dot"
                style={{ backgroundColor: domain.color }}
              />
              <h3>{domain.name}</h3>
              {!domain.is_active && (
                <span className="inactive-badge">Inactive</span>
              )}
            </div>
            <p className="domain-description">
              {domain.description || 'No description'}
            </p>
            <div className="domain-stats">
              <div className="stat">
                <span className="stat-value">{domain.ruleCount}</span>
                <span className="stat-label">Rules</span>
              </div>
              <div className="stat">
                <span className="stat-value">{domain.userCount}</span>
                <span className="stat-label">Users</span>
              </div>
            </div>
            <div className="domain-actions">
              <button
                className="btn-small btn-secondary"
                onClick={() => openUsersModal(domain)}
              >
                View Users
              </button>
              <button
                className="btn-small btn-secondary"
                onClick={() => openEditModal(domain)}
              >
                Edit
              </button>
              {domain.is_active && domain.name !== 'General' && (
                <button
                  className="btn-small btn-danger"
                  onClick={() => deleteDomain(domain.id)}
                >
                  Deactivate
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create Domain Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Create New Domain</h3>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Lending, Insurance, KYC"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe this domain's purpose"
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>Color</label>
              <div className="color-picker">
                {colorOptions.map(color => (
                  <button
                    key={color.value}
                    className={`color-option ${formData.color === color.value ? 'selected' : ''}`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={createDomain} disabled={loading}>
                {loading ? 'Creating...' : 'Create Domain'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Domain Modal */}
      {showEditModal && selectedDomain && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit Domain: {selectedDomain.name}</h3>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Lending, Insurance, KYC"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe this domain's purpose"
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>Color</label>
              <div className="color-picker">
                {colorOptions.map(color => (
                  <button
                    key={color.value}
                    className={`color-option ${formData.color === color.value ? 'selected' : ''}`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={updateDomain} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Domain Users Modal */}
      {showUsersModal && selectedDomain && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <h3>Users in {selectedDomain.name}</h3>
            {loading ? (
              <div className="loading">Loading users...</div>
            ) : domainUsers.length === 0 ? (
              <p className="empty-state">No users assigned to this domain</p>
            ) : (
              <table className="management-table compact">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Global Role</th>
                    <th>Domain Role</th>
                  </tr>
                </thead>
                <tbody>
                  {domainUsers.map(user => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.name}</td>
                      <td>{user.global_role}</td>
                      <td>{user.domain_role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowUsersModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainManagement;
